import React, { useState, useCallback, useContext, useEffect } from "react";
import {
  Button,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  VStack,
  Tooltip,
  useBreakpointValue,
  Flex,
  Box,
} from "@chakra-ui/react";
import CustomInput from "../customInput/CustomInput";
import { debounce } from "lodash";
import { FaFilter } from "react-icons/fa";
import CustomDateRangeMobile from "App/BsesPoc/components/common/CustomDateRangeMobile/CustomDateRangeMobile";
import { SearchIcon } from "@chakra-ui/icons";
import { AppContext } from "AppProvider/AppContext";

interface DropdownOption {
  value: string;
  label: string;
}

interface Dropdown {
  label: string;
  isMultiSelect: boolean;
  key: string;
  options: DropdownOption[];
  placeholder?: string;
}

interface MultiDropdownProps {
  search?: any;
  title?: string;
  dropdowns: Dropdown[];
  selectedOptions: { [key: string]: DropdownOption[] };
  onDropdownChange: (value: any, label: string) => void;
  onApply: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  isTable: boolean;
  handleGet: () => void;
  dateFrom: any;
  dateTo: any;
  setFromDate: React.Dispatch<React.SetStateAction<any>>;
  setToDate: React.Dispatch<React.SetStateAction<any>>;
  resetFilters?: any;
  minH?: any;
  tableObject?: any;
}

const MultiDropdown = ({
  search,
  title,
  dropdowns,
  selectedOptions,
  onDropdownChange,
  onApply,
  onSearchChange,
  isTable,
  handleGet,
  resetFilters,
  dateFrom,
  dateTo,
  setFromDate,
  setToDate,
  minH,
  tableObject,
}: MultiDropdownProps) => {
  const { workFlowData } = useContext(AppContext);
  const [inputValue, setInputValue] = useState("");
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  const handleStartDateChange = (startDate: Date) => {
    setFromDate(startDate);
  };

  const handleEndDateChange = (endDate: Date) => {
    setToDate(endDate);
  };

  const debouncedSearchChange = useCallback(
    debounce((value: string) => {
      onSearchChange(value);
    }, 1000),
    [onSearchChange]
  );

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      debouncedSearchChange(value);
    },
    [setInputValue, debouncedSearchChange]
  );

  const calculateDateFrom = () => {
    const dateRange = workFlowData?.dateFilter?.dateRange;
    const today = new Date();

    if (dateRange) {
      const endMonth = parseInt(dateRange.split("-")[1], 10);
      today.setMonth(today.getMonth() - endMonth);
    } else {
      today.setMonth(today.getMonth() - 6);
    }

    return today;
  };

  const resetFilterss = () => {
    resetFilters();
    setInputValue("");
    if (!viewerWidth && isTable === true && tableObject?.dateActive) {
      const newDateFrom = calculateDateFrom();
      setFromDate(newDateFrom);
      setToDate(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate());
        return tomorrow;
      });
    }
  };

  const viewerWidth = useBreakpointValue({ lg: true });
  
  return (
    <Popover
      isOpen={isPopoverOpen}
      onClose={handlePopoverClose}
      placement="bottom-start"
    >
      <PopoverTrigger>
        {/* <Tooltip hasArrow label={title ? title : "Add Filter"} placement="left"> */}
        <Button
          fontSize="sm"
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
          border="2px solid"
          borderColor="teal.300"
          color="white"
          bg="teal.300"
          w={{ base: "auto", lg: "3rem" }}
          _hover={{ bg: "teal.400", borderColor: "teal.400", color: "white" }}
        >
          {title ? title : <FaFilter fontSize="1.2rem" />}
        </Button>
        {/* </Tooltip> */}
      </PopoverTrigger>
      <PopoverContent p={3} bg="white" borderColor="gray.300" boxShadow="md">
        <PopoverHeader
          mt={-1}
          fontWeight="bold"
          borderBottomWidth="1px"
          color="teal.400"
        >
          Select Options
        </PopoverHeader>
        <PopoverBody>
          <VStack rowGap={1} align="stretch">
            {!viewerWidth && isTable === true && tableObject?.dateActive && (
              <Flex gap={2}>
                <CustomDateRangeMobile
                  startDate={dateFrom}
                  endDate={dateTo}
                  onStartDateChange={handleStartDateChange}
                  onEndDateChange={handleEndDateChange}
                />

                <Box>
                  <Tooltip hasArrow textAlign={"center"} label="Search">
                    <Button
                      bg="teal.300"
                      color="white"
                      w={{ base: "auto", lg: "3rem" }}
                      _hover={{ bg: "teal.400" }}
                      onClick={() => handleGet()}
                    >
                      <SearchIcon />
                    </Button>
                  </Tooltip>
                </Box>
              </Flex>
            )}
            {search && search?.visible && (
              <Input
                placeholder={search?.placeholder || "Search"}
                value={inputValue}
                onChange={handleInputChange}
                borderRadius="md"
                bg="white"
                borderColor="gray.300"
                w="100%"
                _focus={{ borderColor: "blue.500", boxShadow: "outline" }}
              />
            )}
            {dropdowns.map((dropdown: Dropdown, index: number) => {
              return (
                <CustomInput
                  label={dropdown.label}
                  isClear
                  isSearchable={false}
                  name="select"
                  type="select"
                  isMulti={dropdown.isMultiSelect }
                  key={index}
                  options={dropdown.options}
                  placeholder={dropdown.placeholder || "Select Option"}
                  value={selectedOptions[dropdown.key] || null}
                  onChange={(selected) => {
                    onDropdownChange(selected, dropdown.key);
                  }}
                  minH={
                    selectedOptions[dropdown?.key]?.length > 3
                      ? "65px"
                      : "38px"
                  }
                />
              );
            })}
            <Button
              colorScheme="teal"
              // _hover={{ bg: "blue.500" }}
              onClick={() => {
                onApply();
                handlePopoverClose();
              }}
              mt={2}
            >
              Apply
            </Button>
            {resetFilters && (
              <Button
                // variant="outline"
                mt={1}
                onClick={() => resetFilterss()}
                bg="white"
                color="red.500"
                border="2px solid"
                borderColor="red.400"
                _hover={{ bg: "red.50" }}
              >
                Reset Filter
              </Button>
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default MultiDropdown;
